import { InfoTextProps } from './info/info-text';
import { ValidationProps } from 'ui';
import { ReactNode } from 'react';

/**
 *
 * @interface ComplexValue<T>
 *
 * Where T is the data type of the .value property
 *
 * # Description
 *
 * From the server, component values arrive in a JSON Code object format
 *
 * ```
 * {
 *    D?: <description text>,
 *    V: <value>,
 *    I: <icon name/url>,
 *    B: <Background Color as RGB>
 *    F: <Foreground color as RGB>
 * }
 * ```
 *
 * From a consumer perspective, we do not wish to take resposibility to investigate if the component receiving the data does
 *
 *   - array of data |or| single value
 *   - value property |or| description, others....
 *   - checks for empty arrays, or filters out `null` values
 *   - checks for undefined properties
 *   - checks for undefined strings
 *
 * We instead map the data into a new format to allow the components to trust the data they receive
 *
 *  - `value` is always injected into a component
 *  - `valueItem` is always injected. This is always the complete first item of the `valueItems` array
 *  - `valueItems` is always injected with data from the fields values array, or an empty array
 *
 *  ## Implementation notes
 *
 *  - Values provided to the component have been parsed (by vanilla JSON parsing rules + custom Date parsing)
 *  data type when the JSON data is retreived.
 *
 *  - Components operating on single primitive value implement a `value` property. this is always the `.V` property
 *    of the Code format, but casted into a type when possible. Possible types: string, number, Date, boolean
 *
 *  - Components operating on multiple values implement a `values` property. This is always an Array of object literals
 *    in the format of ComplexValue[] e.g `[ { value: string|number|Date , description?: string, icon: 'i.gif' } ]`
 *
 */
export interface ComplexValue<T extends ValueProperty = number> {
  /**
   * `value` is the persisted id of the selected value object.
   *
   * `value` is always injected into a component
   *
   *  Value is mapped from the `V` (Value) property of the `valueItem` object.
   *
   *      - `value` may be one of types: string | boolean | number | Date
   *      - `value` is always defined for strings, booleans and number components.
   *      - `value` will only be `undefined` for undefined Date values.
   *      - For string, the default value is `''`
   *      - For numbers, the default value is `0`
   *      - For Date, the default value is `undefined`
   *      - For boolean, the default value is `false`
   */
  readonly value: T;
  /**
   * `description` is the persisted **display value** for the selected value object.
   *
   *      - Optional. Not guaranteed to be present.
   *      - Always of type string;
   *      - Never of types null or undefined
   */
  readonly description?: string;
  /**
   * `longDescription` is the optional long description of a selected value object.
   *
   *      - Optional. Not guaranteed to be present.
   *      - Always of type string;
   *      - Never of types null or undefined
   */
  readonly longDescription?: string;
  /**
   * `icon` is a URL string that is peristed.
   *
   * The URL is possible to render as-is without base URL or other custom handlings.
   */
  readonly icon?: string;
  /**
   * `backgroundColor` is a HEX value representing a RGB color.
   */
  readonly backgroundColor?: number;
  /**
   * `foregroundColor` is a HEX value representing a RGB color.
   */
  readonly foregroundColor?: number;
  readonly descendants?: ComplexValue<T>[];
  readonly allLevelDescription?: string;
  readonly minLevel?: number;
  readonly categoryId?: number;
  readonly layoutTypeContext?: number;
}

/**
 * @interface ComponentProps
 *
 * Configuration prefix in attributes: `'component.'`
 */
export interface ComponentProps {
  /**
   * The persisted unique id reference for this component instance.
   *
   * Implementations must assign this id to the id attribute of the element bound to the `value` property.
   *
   * The name 'id' cannot be used as it would assign the HTML id attribute to the first component rendered.
   *
   * `componentId` is a Primary Key in the database configuration table for DOMAIN TYPE.
   *
   * Automated UI tests use this id to look up components once they are rendered.
   */
  readonly componentId: string;
  /**
   * CSS class.
   *
   * All components receive a CSS class name based on their constructor name for later customization purpose.
   */
  readonly className: string;
  /**
   * The display label in front of the control
   */
  readonly label?: LabelConfig;
  /**
   * The short help information/hint displayed next to the component that may or may not be hidden by default
   */
  readonly info?: InfoTextProps;
  /**
   * Extensive help information that may or may not be hidden by default
   */
  readonly infoLong?: InfoTextProps;
  /**
   * Optional formatter function that components can use to ensure their `value` data
   * is displayed * according to the consuming applications user or application settings.
   */

  formatDate?: (dt: Date, options?: Intl.DateTimeFormatOptions) => string;
  /**
   * Outputs a string representation of a Date as time display according to current browser locale
   */
  formatTime?: (dt: Date, options?: Intl.DateTimeFormatOptions) => string;
  /**
   * Outputs a string representation of number display according to current browser locale
   */
  formatNumber?: (n: number, options?: Intl.NumberFormatOptions) => string;
  /**
   * Creates a UTC date with the given optional initial dateTime values.
   * Defaults to now as UTC when no arguments are supplied.
   */
  readonly createDate?: (dateValues?: {
    year?: number;
    month?: number;
    day?: number;
    hours?: number;
    minutes?: number;
    seconds?: number;
    milliseconds?: number;
  }) => Date;
  /**
   * Parses string as UTC Date and time. If string cannot be parsed, undefined is returned.
   */
  readonly parseDate?: (dateString: string) => Date | undefined;
  /**
   * Takes a Date initialized with local datetime, and makes it a UTC date with the passed in Date's local datetime values
   */
  readonly cloneDate?: (date: Date) => Date;

  /**
   * Optional. Defaults to undefined.
   * Validation errors for descendants.
   */
  validationDescendants?: ValidationProps[];

  children?: React.ReactNode;
}

/**
 * Type for a `value` property
 */
export type SingleValue = string | number | Date | boolean;

/**
 * Type for `value` property within a ComplexValue shape
 *
 * Currently an exact copy of [[SingleValue]]
 */
export type ValueProperty = SingleValue;

/**
 * Required properties for all components **receiving user input**
 *
 * Configuration prefix in attributes: `'component.'`
 */
export interface EditableComponentProps<T> extends ComponentProps {
  /**
   * Callback function the component should fire when user has modifed the current value.
   * This callback will typically dispatch a redux action on the consumer side so there is no return value;
   */
  readonly onChange: (value: T) => void;
  /**
   * Default is undefined.
   * Callback function the component should fire when on focus cursor.
   */
  readonly onFocus?: (value: any) => void;
  /**
   * Optional. Default is undefined.
   * Callback function the component should fire when focus is lost and the cursor leaves the field.
   * If the function returns `false` then the value entered is invalid and can not be persisted.
   * Typical usage would be component controlled async validation.
   */
  readonly onBlur?: (value: T) => boolean;
  /**
   * Optional. Default is `false`.
   * If the component should display in a read-only state. This means not allowed to edit but being able to copy the current value.
   */
  readOnly?: boolean;
  /**
   * Optional. Default is `false`.
   * If the component should require a valid input value from the user and display a validation message if value is not entered.
   */
  required?: boolean;
  /**
   * Optional. Defaults to undefined.
   * Validation messages to display and for what display level (warning, error, info)
   */
  validation?: ValidationProps;
  /**
   * Optional. Defaults to empty string.
   * A placeholder text to display in the case of no user input, describing e.g the date format to use for dates.
   */
  placeholder?: string;
  /**
   * Optional: Default is undefined.
   * It gives possibility to set autofocus on editable component when DOM renders.
   */
  focus?: boolean;
}

/**
 * @interface RowContainerComponentProps
 * Required properties for "grid" components.
 */
export interface RowContainerComponentProps extends ComponentProps {
  /**
   * CSS class name that will be given to each row in the container.
   */
  rowClassName?: string;

  /**
   * Callback function the component should fire when user wants to add a row.
   */
  onAddDocumentItem?: (selectAddedItem?: boolean) => void;

  /**
   * If new items can be added
   */
  enabledChildEntityOperations?: { add: boolean };

  /**
   * Optional. Defaults to undefined.
   * Validation messages to display and for what display level (warning, error, info)
   */
  validation?: ValidationProps;

  /**
   * Optional. Default is `false`.
   * If the component should display in a read-only state. This means not allowed to edit but being able to copy the current value.
   */
  readOnly?: boolean;
}

/**
 * @interface RowComponentProps
 * Required properties for "row" components.
 */
export interface RowComponentProps extends ComponentProps {
  /**
   * Unique key for this row.
   */
  primaryKey?: ComplexValue<number>;
  /**
   * Indicates whether a row should be possible to remove.
   */
  canBeRemoved?: boolean;
  /**
   * Select an entity. Passing explicit null as path closes the current selection.
   */
  onSelectDocumentItem?: (path?: any) => void;
  /**
   * Callback function the component should fire when user wants to edit a row.
   */
  onEditDocumentItem?: (selectItem?: boolean) => void;
  /**
   * Callback function the component should fire when user has removed a row.
   */
  onDeleteDocumentItem?: () => void;
  /**
   * Callback function the component should fire when user has copied a row.
   */
  onCopyDocumentItem?: () => void;
  /**
   * A text property to display for the remove "row" button.
   */
  removeLabelText?: string;
  /**
   * The components to render when the item is activated.
   */
  details?: React.ReactNode;
  /**
   * Whether this row is currently activated in the iterator.
   */
  activate?: boolean;
  /**
   * Allowed operations on this row.
   */
  enabledChildEntityOperations?: { remove: boolean };
}

/**
 * @interface Options<T> where t is the value property of the options list objects.
 * @private
 *
 * Use case: component dependent on a `options` property: Dropdown, Autocomplete, Search, ...
 *
 */
export interface Options<T extends ValueProperty> {
  /**
   * The list of options the component can display in list view
   */
  options: ComplexValue<T>[];
  /**
   * It describes for which conditions options are loaded.
   */
  loadOptions?: LoadOptions;
  /**
   * If options failed to load because of network or server error, this string will be set.
   */
  loadOptionsError?: boolean;
  /**
   * Callback when chooses to select from a list of possible values
   */
  onLoadOptions?: (loadOptions: LoadOptions) => void;
  onLoadOptionsDelay?: number;
  displaySelectAll?: boolean;
  /**
   * Flag which pass information about whether options are fetching.
   */
  fetching?: boolean;
  loadCategoryOptions?: LoadOptions;
  categoryOptions?: ComplexValue<T>[];
  onLoadCategoryOptions?: (loadOptions: LoadOptions) => void;
  locale?: string;
}
/**
 * @interface LoadOptions
 * @private
 *
 * How results should be returned when user requests a list of possible component input values.
 */
export interface LoadOptions {
  /**
   * When left un-set this will default to the domainId of the componentDescriptor.
   */
  domainId?: number;
  /**
   * Value which come from input in 'select' components.
   *
   * Now we have query property, it is the same like api call param, so I removed searchTerm property.
   */
  query?: string;
  sort?: 'asc' | 'desc';
  /**
   * Possible match strategies
   *
   *  - `'contains'` : all texts in the result lists contains the text user entered.
   *  - `'startsWith'`: all texts in the result starts with the text user entered.
   */
  match?: 'contains' | 'startsWith';
  parentId?: number;
  siblingId?: number;
  levels?: number;
  consequenceType?: number;
  /**
   * In some contexts it is possible to select a subset of options based on a category.
   * This code specifies the subset to return.
   */
  categoryId?: number;
  /**
   * Optional. Default is `0`.
   *
   * Starting index of the matching result to return, from the result list.
   */
  offset?: number;
  /**
   * Optional: Default is `undefined`.
   *
   * The max number of items to return in the result list.
   */
  limit?: number;
  /**
   * Whether the `Not Selected` code, if it exists, should be included in the result.
   */
  displayNotSelected?: boolean;
  /**
   * In some contexts it is possible to select a subset of options based on the id of the contextual entity.
   * This code specifies the subset to return.
   */
  iteratorId?: number;

  limitFieldsTo?: any[];
  /**
   * Return only those options that are leaf nodes.
   */
  leafNodesOnly?: boolean;
  includeDisabled?: boolean;
}

/**
 * Components that allow the user to enter a single value, where T is the data type of the `value` property.
 *
 * Use case: NumberBox, TextArea, etc
 *
 * @example
 *
 * Example usage: NumberBoxProps
 *
 * > only component specific props are present.
 *
 *  T=number hints that:
 *  - the value provided in the `value` property will be a number type
 *  - the value passed from onChange is a number
 *
 * interface NumberBoxProps extends SingleValueComponent<number> {
 *   maxInteger?: number;
 *   maxDecimals?: number;
 *   isFloat?: boolean;
 *   isPositive?: boolean;
 *   isNegative?: boolean;
 *   thousand?: boolean;
 * }
 */
export interface SingleValueComponent<T extends SingleValue>
  extends EditableComponentProps<T> {
  value?: T;
}
/**
 * Component that allows the user to select a single [[ComplexValue]] object as input value.
 *
 * Use case: single select components that do async lookups
 */
export interface SingleKeyValueComponent<T extends ValueProperty>
  extends EditableComponentProps<ComplexValue<T>>,
  Options<T> {
  valueItem: ComplexValue<T>;
}

/**
 * Component that allows the user to select multiple [[ComplexValue]] objects as input value.
 *
 * Use case: multi select component that do async lookups. T is the Value type for the selected item
 */
export interface MultiKeyValueComponent<T extends ValueProperty>
  extends EditableComponentProps<ComplexValue<T>[]>,
  Options<T> {
  valueItems: ComplexValue<T>[]; // note: TS 2+ has ReadonlyArray<T>
}

/**
 * The shape of config settings for the domain attribute `label`
 *
 * Any domain in the config may define a visible or hidden label, with optional icon, required and text properties
 */
export interface LabelConfig {
  /**
   * Define whether the content of component should be visible. Default is 'undefined'.
   */
  visible?: boolean;
  /**
   * Define content of component. Default is 'undefined'.
   */
  text?: string;
  /**
   * Show asterisk after content of component. Default is 'undefined'.
   */
  required?: boolean;
  /**
   * Show icon with a label. Must be defined as CSS class. Default is 'undefined'
   */
  icon?: string;
  /**
   * Show colon after label text. Default is false
   */
  colon?: boolean;

  /**
   * @internal
   */
  children?: ReactNode;
  meatballPanelConfiguration?: MeatballPanelConfiguration;
  className?: string;
}

export const enum DateFormat {
  Numeric = 'numeric',
  DoubleDigit = '2-digit',
  Long = 'long',
  Short = 'short',
  Narrow = 'narrow'
}

export type MonthFormat = `${DateFormat.Numeric}` | `${DateFormat.DoubleDigit}`
  | `${DateFormat.Long}` | `${DateFormat.Short}` | `${DateFormat.Narrow}`;
export type WeekdayFormat = `${DateFormat.Long}` | `${DateFormat.Short}` | `${DateFormat.Narrow}`;
export type DayFormat = `${DateFormat.Numeric}` | `${DateFormat.DoubleDigit}`;
export type YearFormat = `${DateFormat.Numeric}` | `${DateFormat.DoubleDigit}`;

export interface MeatballPanelConfiguration {
  meatball?: React.ReactNode;
  panel?: React.ReactNode;
}
